import { useState, useEffect } from 'react'
import { MdArrowBack, MdBusiness, MdSave, MdCancel, MdPhone, MdTrendingUp } from 'react-icons/md'
import { useParams, useNavigate } from 'react-router-dom'
import { Button, LoadingSpinner, InfoCard } from '../../components/ui'
import { useAirline } from '../../hooks/useAirline'
import { formatDate } from '../../utils/airline'

// Add custom styles for dark mode inputs
const darkModeStyles = `
  .dark input {
    background-color: #1f2937 !important;
    color: #ffffff !important;
  }
  .dark input::placeholder {
    color: #9ca3af !important;
  }
  .dark select {
    background-color: #1f2937 !important;
    color: #ffffff !important;
  }
`

function AirlineFormPage() {
  const { airlineId } = useParams()
  const navigate = useNavigate()
  const isEditing = !!airlineId

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    country: '',
    logo: '',
    website: '',
    phone: '',
    email: '',
    status: 'active',
    iataCode: '',
    icaoCode: '',
    callsign: '',
    founded: '',
    alliance: '',
    fleetSize: '',
    destinations: [],
    hubAirports: [],
    headquarters: {
      city: '',
      country: '',
      address: '',
    },
    frequentFlyerProgram: {
      name: '',
      website: '',
    },
    contactInfo: {
      customerService: {
        phone: '',
        email: '',
        hours: '',
      },
      reservations: {
        phone: '',
        email: '',
        hours: '',
      },
      cargo: {
        phone: '',
        email: '',
      },
    },
    financialInfo: {
      currency: 'USD',
      annualRevenue: '',
      fiscalYear: new Date().getFullYear().toString(),
    },
    regulatoryInfo: {
      operatingCertificate: '',
      certificateExpiry: '',
      safetyRating: 'Good',
    },
    socialMedia: {
      facebook: '',
      twitter: '',
      instagram: '',
      linkedin: '',
    },
  })

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const {
    getAirlineAsync,
    createAirlineAsync,
    updateAirlineAsync,
    isCreateAirlineLoading,
    isUpdateAirlineLoading,
  } = useAirline()

  useEffect(() => {
    if (isEditing && airlineId) {
      loadAirline()
    }
  }, [airlineId, isEditing]) // eslint-disable-line react-hooks/exhaustive-deps

  const loadAirline = async () => {
    try {
      setIsLoading(true)
      const airlineData = await getAirlineAsync(airlineId)
      if (airlineData) {
        setFormData({
          name: airlineData.name || '',
          code: airlineData.code || '',
          country: airlineData.country || '',
          logo: airlineData.logo || '',
          website: airlineData.website || '',
          phone: airlineData.phone || '',
          email: airlineData.email || '',
          status: airlineData.status || 'active',
          iataCode: airlineData.iataCode || '',
          icaoCode: airlineData.icaoCode || '',
          callsign: airlineData.callsign || '',
          founded: airlineData.founded ? formatDate(airlineData.founded, 'en-CA') : '',
          alliance: airlineData.alliance || '',
          fleetSize: airlineData.fleetSize || '',
          destinations: airlineData.destinations || [],
          hubAirports: airlineData.hubAirports || [],
          headquarters: airlineData.headquarters || {
            city: '',
            country: '',
            address: '',
          },
          frequentFlyerProgram: airlineData.frequentFlyerProgram || {
            name: '',
            website: '',
          },
          contactInfo: airlineData.contactInfo || {
            customerService: { phone: '', email: '', hours: '' },
            reservations: { phone: '', email: '', hours: '' },
            cargo: { phone: '', email: '' },
          },
          financialInfo: airlineData.financialInfo || {
            currency: 'USD',
            annualRevenue: '',
            fiscalYear: new Date().getFullYear().toString(),
          },
          regulatoryInfo: airlineData.regulatoryInfo || {
            operatingCertificate: '',
            certificateExpiry: '',
            safetyRating: 'Good',
          },
          socialMedia: airlineData.socialMedia || {
            facebook: '',
            twitter: '',
            instagram: '',
            linkedin: '',
          },
        })
      }
    } catch (error) {
      console.error('Error loading airline:', error)
      setError('Failed to load airline details')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleNestedInputChange = (parent, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [field]: value,
      },
    }))
  }

  // const handleDeepNestedInputChange = (parent, child, field, value) => {
  //   setFormData(prev => ({
  //     ...prev,
  //     [parent]: {
  //       ...prev[parent],
  //       [child]: {
  //         ...prev[parent]?.[child],
  //         [field]: value
  //       }
  //     }
  //   }))
  // }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      setIsLoading(true)
      setError(null)

      const submitData = {
        ...formData,
        fleetSize: parseInt(formData.fleetSize) || 0,
        annualRevenue: parseInt(formData.financialInfo.annualRevenue) || 0,
      }

      if (isEditing) {
        await updateAirlineAsync(airlineId, submitData)
      } else {
        await createAirlineAsync(submitData)
      }

      navigate('/airlines')
    } catch (error) {
      console.error('Error saving airline:', error)
      setError('Failed to save airline')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    navigate(`/airlines/${airlineId}`)
  }

  const handleCancel = () => {
    navigate('/airlines')
  }

  if (isLoading && isEditing) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Add custom styles for dark mode */}
      <style>{darkModeStyles}</style>

      {/* Header */}
      <div className="flex justify-start mb-4">
        <Button variant="ghost" onClick={handleBack} className="flex items-center space-x-2">
          <MdArrowBack />
          <span>Back</span>
        </Button>
      </div>
      <div className="mb-6">
        <div className="flex items-center space-x-4">
          <div className="flex-1 min-w-0 text-left">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
              {isEditing ? 'Edit Airline' : 'Add New Airline'}
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              {isEditing ? 'Update airline information' : 'Create a new airline in the system'}
            </p>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="bg-error-100 dark:bg-error-900 border border-error-300 dark:border-error-700 text-error-800 dark:text-error-200 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Basic Information */}
        <InfoCard
          title="Basic Information"
          icon={MdBusiness}
          iconBgColor="bg-primary-100 dark:bg-primary-900"
          iconColor="text-primary-600 dark:text-primary-400"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Airline Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter airline name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Country *
              </label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                required
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter country"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                IATA Code
              </label>
              <input
                type="text"
                value={formData.iataCode}
                onChange={(e) => handleInputChange('iataCode', e.target.value.toUpperCase())}
                maxLength={3}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., AA"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                ICAO Code
              </label>
              <input
                type="text"
                value={formData.icaoCode}
                onChange={(e) => handleInputChange('icaoCode', e.target.value.toUpperCase())}
                maxLength={4}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., AAL"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Callsign
              </label>
              <input
                type="text"
                value={formData.callsign}
                onChange={(e) => handleInputChange('callsign', e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., SPEEDBIRD"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Status
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="suspended">Suspended</option>
                <option value="bankrupt">Bankrupt</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Founded Date
              </label>
              <input
                type="date"
                value={formData.founded}
                onChange={(e) => handleInputChange('founded', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Fleet Size
              </label>
              <input
                type="number"
                value={formData.fleetSize}
                onChange={(e) => handleInputChange('fleetSize', e.target.value)}
                min="0"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Number of aircraft"
              />
            </div>
          </div>
        </InfoCard>

        {/* Contact Information */}
        <InfoCard
          title="Contact Information"
          icon={MdPhone}
          iconBgColor="bg-info-100 dark:bg-info-900"
          iconColor="text-info-600 dark:text-info-400"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => handleInputChange('website', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="https://example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="contact@airline.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Phone
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="+1-555-123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Alliance
              </label>
              <select
                value={formData.alliance}
                onChange={(e) => handleInputChange('alliance', e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">None</option>
                <option value="Star Alliance">Star Alliance</option>
                <option value="Oneworld">Oneworld</option>
                <option value="SkyTeam">SkyTeam</option>
              </select>
            </div>
          </div>
        </InfoCard>

        {/* Financial Information */}
        <InfoCard
          title="Financial Information"
          icon={MdTrendingUp}
          iconBgColor="bg-warning-100 dark:bg-warning-900"
          iconColor="text-warning-600 dark:text-warning-400"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Currency
              </label>
              <select
                value={formData.financialInfo.currency}
                onChange={(e) =>
                  handleNestedInputChange('financialInfo', 'currency', e.target.value)
                }
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="JPY">JPY</option>
                <option value="CAD">CAD</option>
                <option value="AUD">AUD</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Annual Revenue
              </label>
              <input
                type="number"
                value={formData.financialInfo.annualRevenue}
                onChange={(e) =>
                  handleNestedInputChange('financialInfo', 'annualRevenue', e.target.value)
                }
                min="0"
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Revenue in currency"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                Fiscal Year
              </label>
              <input
                type="text"
                value={formData.financialInfo.fiscalYear}
                onChange={(e) =>
                  handleNestedInputChange('financialInfo', 'fiscalYear', e.target.value)
                }
                className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white text-neutral-900 placeholder-neutral-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="e.g., 2025"
              />
            </div>
          </div>
        </InfoCard>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-4 pt-6 border-t border-neutral-200 dark:border-neutral-700">
          <Button type="button" variant="ghost" onClick={handleCancel} disabled={isLoading}>
            <MdCancel className="mr-2" />
            Cancel
          </Button>

          <Button
            type="submit"
            variant="primary"
            disabled={isLoading || isCreateAirlineLoading || isUpdateAirlineLoading}
            className="flex items-center space-x-2"
          >
            <MdSave />
            <span>{isEditing ? 'Update' : 'Create'} Airline</span>
          </Button>
        </div>
      </form>
    </div>
  )
}

export default AirlineFormPage
